import { coerceAgentOrderDraft } from '@hero/hooks-shared';
import type { PendingConfirmation } from './types';

export interface ClientOrderBreakdown {
  clientName: string;
  orderCount: number;
}

export interface PendingBatchSummary {
  totalOrders: number;
  cardCount: number;
  clients: ClientOrderBreakdown[];
  /** Human-readable one-liner, e.g. "20 orders — Berain 2, Arasco 3, and 15 others" */
  line: string;
}

const INTERNAL_LABEL = 'Internal';

const countOrdersInPayload = (payload: Record<string, unknown>): number => {
  try {
    const draft = coerceAgentOrderDraft(payload);
    const sum = draft.destinations.reduce(
      (acc, d) => acc + (d.vehicleQuantity ?? 1),
      0,
    );
    return sum > 0 ? sum : 1;
  } catch {
    return 1;
  }
};

const readClientName = (pending: PendingConfirmation): string => {
  const name = String(pending.payload.clientName ?? pending.payload.name ?? '').trim();
  return name || INTERNAL_LABEL;
};

/**
 * Aggregate pending confirmation cards into a scannable one-liner for
 * multi-client bulk review — "20 orders — Berain 2, Arasco 3, and
 * 15 others". Used above the confirmation stack so the user sees the
 * full picture before hitting Confirm all.
 */
export const summarizePendingBatch = (
  confirmations: PendingConfirmation[],
): PendingBatchSummary => {
  // Driver-allocation batches aren't client-keyed — summarize by count.
  if (
    confirmations.length > 0 &&
    confirmations.every(p => p.action === 'assign_driver_to_order')
  ) {
    const n = confirmations.length;
    return {
      totalOrders: n,
      cardCount: n,
      clients: [],
      line: `${n} driver ${n === 1 ? 'assignment' : 'assignments'}`,
    };
  }

  const byClient = new Map<string, number>();

  for (const pending of confirmations) {
    const clientName = readClientName(pending);
    const orders =
      pending.action === 'create_order'
        ? countOrdersInPayload(pending.payload)
        : 1;
    byClient.set(clientName, (byClient.get(clientName) ?? 0) + orders);
  }

  const clients = [...byClient.entries()]
    .map(([clientName, orderCount]) => ({ clientName, orderCount }))
    .sort((a, b) => b.orderCount - a.orderCount);

  const totalOrders = clients.reduce((sum, c) => sum + c.orderCount, 0);
  const line = formatBatchSummaryLine(totalOrders, clients);

  return {
    totalOrders,
    cardCount: confirmations.length,
    clients,
    line,
  };
};

export const readPendingClientName = readClientName;

const formatBatchSummaryLine = (
  totalOrders: number,
  clients: ClientOrderBreakdown[],
): string => {
  const orderWord = totalOrders === 1 ? 'order' : 'orders';

  if (clients.length === 0) {
    return `${totalOrders} ${orderWord}`;
  }

  if (clients.length === 1) {
    const { clientName, orderCount } = clients[0];
    return `${orderCount} ${orderCount === 1 ? 'order' : 'orders'} for ${clientName}`;
  }

  const [first, second, ...rest] = clients;
  const restOrders = rest.reduce((sum, c) => sum + c.orderCount, 0);

  if (rest.length === 0) {
    return `${totalOrders} ${orderWord} — ${first.clientName} ${first.orderCount}, ${second.clientName} ${second.orderCount}`;
  }

  const othersWord = restOrders === 1 ? 'other' : 'others';
  return `${totalOrders} ${orderWord} — ${first.clientName} ${first.orderCount}, ${second.clientName} ${second.orderCount}, and ${restOrders} ${othersWord}`;
};
